// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { isIOS } from '../../lib/useragent';
import theme from '../../styled-components/dark';
import { normalize } from '../../utils/number';
import { type ObjectValuesUnionType } from '../../utils/typing';

const DEFAULT_CURSOR_DIMENSION = 1.3; // em
const DEFAULT_BAR_HEIGHT = 0.3; // em;
const DEFAULT_CURSOR_SPACING = DEFAULT_CURSOR_DIMENSION / 4; // em;

export const SliderTooltipPositions = {
  bottom: 'bottom',
  top: 'top',
};

export type SliderTooltipPosition = ObjectValuesUnionType<typeof SliderTooltipPositions>;

const PaddingWrapper = styled.div`
  position: relative;
  padding: 1em 1em;
`;

export const SliderWrapperClassName = 'slider-wrapper';
const Wrapper = styled.div`
  position: relative;
  font-size: 1rem;

  --Slider-cursorColor: ${theme.Slider.default.cursorColor};
  --Slider-leftBarColor: ${theme.Slider.default.leftBarColor};
  --Slider-rightBarColor: ${theme.Slider.default.rightBarColor};
  --Slider-anchorColor: ${theme.Slider.default.anchorColor};

  --Slider-cursorDimension: ${DEFAULT_CURSOR_DIMENSION}rem;
  --Slider-barHeight: ${DEFAULT_BAR_HEIGHT}em;
  --Slider-cursorSpacing: ${DEFAULT_CURSOR_SPACING}em;

  --Slider-tooltipBackgroundColor: ${theme.Slider.default.tooltipBackgroundColor};
  --Slider-tooltipColor: ${theme.Slider.default.tooltipColor};
  --Slider-tooltipBorderColor: ${theme.Slider.default.tooltipBorderColor};

  width: 100%;
  height: max(var(--Slider-barHeight), var(--Slider-cursorDimension));
  transition: filter 200ms ease-in-out;

  ${({ $disabled }) =>
    $disabled
      ? css`
          pointer-events: none;
          --Slider-cursorColor: ${theme.Slider.disabled.cursorColor};
          --Slider-leftBarColor: ${theme.Slider.disabled.leftBarColor};
          --Slider-rightBarColor: ${theme.Slider.disabled.rightBarColor};
        `
      : null}
`;

const barSharedStyle = css`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: var(--Slider-barHeight);

  cursor: pointer;
  z-index: 1;
`;

const LeftBar = styled.div`
  background-color: var(--Slider-leftBarColor);
  border-top-left-radius: 50em;
  border-bottom-left-radius: 50em;

  ${barSharedStyle}
`;

const Cursor = styled.div`
  position: absolute;
  width: var(--Slider-cursorDimension);
  height: var(--Slider-cursorDimension);
  background-color: var(--Slider-cursorColor);

  border-radius: 100%;
  cursor: pointer;
  z-index: 3;

  user-select: none;
  transform-style: preserve-3d;
  transform: translate3d(0, 0, 0);

  &:before {
    position: absolute;
    content: '';
    left: 50%;
    top: 50%;
    width: 220%;
    height: 220%;
    border-radius: 50%;
    transform: translate3d(-50%, -50%, 0);
    opacity: 0;
    transition:
      opacity 200ms ease-in-out,
      border 200ms ease-in-out;

    background: rgba(0, 0, 0, 0);
    border: 0 solid white;
  }

  ${({ $visible }) =>
    !Target.__MOBILE__ && !$visible
      ? css`
          &:hover {
            &:before {
              border: 0.6em solid white;
              opacity: 0.2;
            }
          }
        `
      : undefined}

  ${({ $visible }) =>
    $visible
      ? css`
          &:after,
          &:before {
            border: 0.45em solid white;
            opacity: 0.4;
          }
        `
      : undefined}
`;

export const SliderRightBarClassName = 'slider-right-bar';
const RightBar = styled.div`
  background-color: var(--Slider-rightBarColor);
  border-top-right-radius: 50em;
  border-bottom-right-radius: 50em;

  ${barSharedStyle}
`;

const Anchor = styled.div`
  position: absolute;
  top: 50%;
  width: 0.25em;
  height: 0.25em;
  z-index: 2;

  border-radius: 100%;
  background-color: var(--Slider-anchorColor);
  transform: translate3d(0, 0, 0);

  ${({ $offsetXRatio }) => css`
    left: ${($offsetXRatio ?? 0) * 100}%;
    transform: translate(-50%, -50%);
  `}

  pointer-events: none;
`;

/**
 * Opacity breaks 3d transforms so we must use a dedicated wrapper to play with it, for more info :
 * https://css-tricks.com/things-watch-working-css-3d/#3-opacity
 */
const TooltipWrapper = styled.div`
  position: absolute;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease-in-out;
  pointer-events: none;
  left: 50%;
  transform: translate3d(0, 0, 0);

  ${({ $tooltipPosition }) =>
    $tooltipPosition === 'top'
      ? css`
          top: -1.25em;
          transform: translate(-50%, -100%);
        `
      : css`
          top: calc(100% + 1.25em);
          transform: translate(-50%, 0);
        `}
`;

const TooltipContent = styled.div`
  padding: 0 10px;
  color: var(--Slider-tooltipColor);
  font-size: 110%;
  font-weight: normal;
  background-color: var(--Slider-tooltipBackgroundColor);
  position: relative;
  border-radius: 0.4em;
`;

const TooltipBorder = styled.div`
  position: absolute;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  left: -1px;
  top: -1px;
  border-radius: 0.4em;
  background-color: var(--Slider-tooltipBorderColor);
`;

const TooltipTriangle = styled.div`
  position: absolute;
  width: 1em;
  height: 1em;
  left: 50%;

  border: 0.1em solid var(--Slider-tooltipBorderColor);
  border-radius: 0.15em;

  ${({ $tooltipPosition }) =>
    $tooltipPosition === 'top'
      ? css`
          top: 100%;
          transform: translate3d(-50%, calc(-50% - 1px), -1px) rotateZ(45deg);
          background: linear-gradient(
            135deg,
            transparent 0%,
            transparent 50%,
            var(--Slider-tooltipBackgroundColor) 50%,
            var(--Slider-tooltipBackgroundColor) 100%
          );
        `
      : css`
          top: 1px;
          transform: translate3d(-50%, -50%, -1px) rotateZ(45deg);
          background: linear-gradient(
            135deg,
            var(--Slider-tooltipBackgroundColor) 0%,
            var(--Slider-tooltipBackgroundColor) 50%,
            transparent 50%,
            transparent 100%
          );
        `}
`;

type SliderProps = {
  className?: string;
  step?: number;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  hideTooltip?: boolean;
  hideAnchors?: boolean;
  formatTooltipLabel?: (value: number) => string;
  tooltipPosition?: SliderTooltipPosition;
};

const Slider = ({
  className = '',
  step,
  min,
  max,
  value,
  onChange,
  disabled = false,
  hideTooltip = false,
  hideAnchors = false,
  formatTooltipLabel,
  tooltipPosition = SliderTooltipPositions.top,
}: SliderProps) => {
  const usedStep = useMemo(() => step ?? 1, [step]);
  const rendered = useRef(false);

  const wrapperRef = useRef();
  const leftBarRef = useRef();
  const cursorRef = useRef();
  const rightBarRef = useRef();
  const isDownRef = useRef();
  const [isDown, setIsDown] = useState(false);

  useEffect(() => {
    isDownRef.current = isDown;
  }, [isDown]);

  const [storedWrapperWidth, setStoredWrapperWidth] = useState(
    wrapperRef?.current?.getBoundingClientRect?.()?.width ?? 0,
  );

  const onValueChange = useCallback(
    (newValue) => {
      if (rendered.current) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const onValueChangeRef = useRef();

  useEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  const anchors = useMemo(() => {
    const normalizedMax = max - min;
    const normMaxByStep = normalizedMax / usedStep;
    const isInteger = (normMaxByStep * 10) % 10 === 0;
    const nbSteps = isInteger ? normMaxByStep + 1 : Math.floor(normMaxByStep + 2);

    if (isInteger) {
      return [...new Array(nbSteps).keys()].map((i) => {
        const val = min + i * usedStep;
        const progress = normalize(val, min, max, 0, 1);

        return {
          value: val,
          progress,
        };
      });
    } else {
      return [
        ...[...new Array(nbSteps - 1).keys()].map((i) => {
          const val = min + i * usedStep;
          const progress = normalize(val, min, max, 0, 1);

          return {
            value: val,
            progress,
          };
        }),
        {
          value: max,
          progress: 1,
        },
      ];
    }
  }, [max, min, usedStep]);

  const anchorsRef = useRef();

  useEffect(() => {
    anchorsRef.current = anchors;
  }, [anchors]);

  const currentAnchorIndex = useMemo(() => {
    let smallerIndex = anchors.findIndex((k) => k.value >= value);

    if (smallerIndex === -1) {
      smallerIndex = anchors.length - 1;
    } else if (smallerIndex !== 0) {
      smallerIndex--;
    }

    const greaterIndex = smallerIndex === anchors.length - 1 ? smallerIndex : smallerIndex + 1;

    const { value: smallerValue } = anchors[smallerIndex];
    const { value: greaterValue } = anchors[greaterIndex];

    if (Math.abs(smallerValue - value) > Math.abs(greaterValue - value)) {
      return greaterIndex;
    }

    return smallerIndex;
  }, [anchors, value]);

  const currentAnchor = useMemo(() => anchors[currentAnchorIndex], [anchors, currentAnchorIndex]);

  useEffect(() => {
    const { progress } = currentAnchor;
    const w = storedWrapperWidth * progress;
    const l = leftBarRef.current;
    const r = rightBarRef.current;
    const c = cursorRef.current;

    if (l) {
      l.style.width = `calc(${w}px - var(--Slider-cursorDimension) / 2 - var(--Slider-cursorSpacing))`;
    }
    if (r) {
      r.style.width = `calc(100% - ${w}px - var(--Slider-cursorDimension) / 2 - var(--Slider-cursorSpacing))`;
      r.style.left = `calc(${w}px + var(--Slider-cursorDimension) / 2 + var(--Slider-cursorSpacing))`;
    }
    if (c) {
      c.style.left = `calc(${w}px - var(--Slider-cursorDimension) / 2)`;
    }
  }, [currentAnchor, storedWrapperWidth]);

  useLayoutEffect(() => {
    const wrapperNode = wrapperRef.current;
    const cursorNode = cursorRef.current;

    const resizeCallback = () => {
      if (wrapperNode) {
        const { width } = wrapperNode.getBoundingClientRect();

        setStoredWrapperWidth(width);
      }
    };

    resizeCallback();

    window.addEventListener('resize', resizeCallback);

    const preventEvent = (e: unknown) => {
      e.preventDefault();
      return false;
    };

    const onDownCallback = () => {
      isDownRef.current = true;
      setIsDown(true);

      document.addEventListener('contextmenu', preventEvent);
    };

    const onUpCallback = () => {
      isDownRef.current = false;
      setIsDown(false);

      document.removeEventListener('contextmenu', preventEvent);
    };

    const computeNextValueBasedOnMouseX = (mouseX: number) => {
      if (wrapperNode && cursorNode) {
        const { left: wrapperLeft, width: wrapperWidth } = wrapperNode.getBoundingClientRect();
        const { width: cursorWidth } = cursorNode.getBoundingClientRect();
        const anchorsUnrefed = anchorsRef.current;
        const normalizedMouseX = mouseX - cursorWidth / 2;
        const onChangeFunc = onValueChangeRef.current;

        if (!onChangeFunc) {
          return;
        }

        if (anchorsUnrefed) {
          if (normalizedMouseX < wrapperLeft) {
            onChangeFunc(anchorsUnrefed[0].value);
          } else if (normalizedMouseX > wrapperLeft + wrapperWidth) {
            onChangeFunc(anchorsUnrefed[anchorsUnrefed.length - 1].value);
          } else {
            const mouseProgress = (normalizedMouseX - wrapperLeft) / wrapperWidth;
            let smallerIndex = anchorsUnrefed.findIndex((k) => k.progress >= mouseProgress);

            if (smallerIndex === -1) {
              smallerIndex = anchorsUnrefed.length - 1;
            } else if (smallerIndex !== 0) {
              smallerIndex--;
            }

            const greaterIndex = smallerIndex === anchorsUnrefed.length - 1 ? smallerIndex : smallerIndex + 1;

            const { progress: smallerProgress, value: smallerValue } = anchorsUnrefed[smallerIndex];
            const { progress: greaterProgress, value: greaterValue } = anchorsUnrefed[greaterIndex];
            const progressGapBetweenGates = greaterProgress - smallerProgress;
            const smallerProgressTriggerPoint = smallerProgress + progressGapBetweenGates / 2;
            const greaterProgressTriggerPoint = greaterProgress - progressGapBetweenGates / 2;

            if (mouseProgress > greaterProgressTriggerPoint) {
              onChangeFunc(greaterValue);
            } else if (mouseProgress < smallerProgressTriggerPoint) {
              onChangeFunc(smallerValue);
            }
          }
        }
      }
    };

    const onMoveCallback = (e: SyntheticTouchEvent<HTMLElement> & SyntheticMouseEvent<HTMLElement>) => {
      if (isDownRef.current) {
        const position = {
          x: e.targetTouches ? e.targetTouches[0].pageX : e.clientX,
          y: e.targetTouches ? e.targetTouches[0].pageY : e.clientY,
        };
        computeNextValueBasedOnMouseX(position.x);

        if (isIOS) {
          e.preventDefault();
        }
      }
    };

    const onWrapperMouseDownCallback = (e: SyntheticTouchEvent<HTMLElement> & SyntheticMouseEvent<HTMLElement>) => {
      const position = {
        x: e.targetTouches ? e.targetTouches[0].pageX : e.clientX,
        y: e.targetTouches ? e.targetTouches[0].pageY : e.clientY,
      };
      computeNextValueBasedOnMouseX(position.x);
    };

    if (cursorNode) {
      cursorNode.addEventListener('mousedown', onDownCallback);
      cursorNode.addEventListener('touchstart', onDownCallback);
    }

    if (wrapperNode) {
      wrapperNode.addEventListener('mousedown', onWrapperMouseDownCallback);
      if (!Target.__MOBILE__) {
        wrapperNode.addEventListener('click', preventEvent);
      }
    }

    window.addEventListener('mouseup', onUpCallback);
    window.addEventListener('touchend', onUpCallback);
    window.addEventListener('touchmove', onMoveCallback, { passive: !isIOS });
    window.addEventListener('mousemove', onMoveCallback, { passive: !isIOS });

    return () => {
      window.removeEventListener('resize', resizeCallback);

      window.removeEventListener('mousedown', onDownCallback);
      window.removeEventListener('mouseup', onUpCallback);
      window.removeEventListener('mousecancel', onUpCallback);
      window.removeEventListener('touchstart', onDownCallback);
      window.removeEventListener('touchend', onUpCallback);
      window.removeEventListener('touchcancel', onUpCallback);
      window.removeEventListener('touchmove', onMoveCallback, { passive: !isIOS });
      window.removeEventListener('mousemove', onMoveCallback, { passive: !isIOS });

      if (cursorNode) {
        cursorNode.removeEventListener('mousedown', onDownCallback);
        cursorNode.removeEventListener('touchstart', onDownCallback);
      }

      if (wrapperNode) {
        wrapperNode.removeEventListener('mousedown', onWrapperMouseDownCallback);
      }
    };
  }, []);

  useEffect(() => {
    rendered.current = true;
  }, []);

  return (
    <PaddingWrapper className={className}>
      <Wrapper className={SliderWrapperClassName} ref={wrapperRef} $disabled={disabled}>
        <LeftBar ref={leftBarRef} />
        <Cursor ref={cursorRef} $visible={isDown}>
          {!hideTooltip ? (
            <TooltipWrapper $tooltipPosition={tooltipPosition} $visible={isDown}>
              <TooltipBorder />
              <TooltipTriangle $tooltipPosition={tooltipPosition} />
              <TooltipContent>{formatTooltipLabel ? formatTooltipLabel(value) : value}</TooltipContent>
            </TooltipWrapper>
          ) : null}
        </Cursor>
        <RightBar className={SliderRightBarClassName} ref={rightBarRef} />
        {step && !hideAnchors
          ? anchors
              .slice(1, anchors.length - 1)
              .map((anchor) => <Anchor key={`slider-anchor-${anchor.progress}`} $offsetXRatio={anchor.progress} />)
          : null}
      </Wrapper>
    </PaddingWrapper>
  );
};

export default Slider;
